// src/features/simulators/shared/utils/apiExport.ts

import type { CapturedRequest } from '../types/simulator.types';

/**
 * Generate a cURL command from a captured request
 */
export const toCurl = (req: CapturedRequest): string => {
  const parts = [`curl -X ${req.method}`];

  Object.entries(req.headers).forEach(([key, value]) => {
    parts.push(`  -H '${key}: ${value}'`);
  });

  if (req.body) {
    parts.push(`  -d '${JSON.stringify(req.body)}'`);
  }

  parts.push(`  '${req.url}'`);

  return parts.join(' \\\n');
};

/**
 * Generate a Postman Collection v2.1 JSON from captured requests
 */
export const toPostmanCollection = (
  requests: CapturedRequest[],
  collectionName: string,
): string => {
  // Group requests by label prefix for folders
  const folders: Record<string, any[]> = {};

  for (const req of requests) {
    const folderName = getFolderName(req.label);
    if (!folders[folderName]) folders[folderName] = [];

    folders[folderName].push({
      name: req.label,
      request: {
        method: req.method,
        header: Object.entries(req.headers).map(([key, value]) => ({
          key,
          value: replaceWithVariables(key, value),
          type: 'text',
        })),
        body: req.body
          ? {
              mode: 'raw',
              raw: JSON.stringify(req.body, null, 2),
              options: { raw: { language: 'json' } },
            }
          : undefined,
        url: {
          raw: replaceUrlWithVariable(req.url),
          host: ['{{baseUrl}}'],
          path: extractPath(req.url).split('/').filter(Boolean),
        },
      },
      response: [
        {
          name: `${req.responseStatus} Response`,
          status: req.responseStatus >= 200 && req.responseStatus < 300 ? 'OK' : 'Error',
          code: req.responseStatus,
          body: JSON.stringify(req.responseBody, null, 2),
          header: [{ key: 'Content-Type', value: 'application/json' }],
        },
      ],
    });
  }

  const collection = {
    info: {
      name: collectionName,
      description: `API requests captured from simulator: ${collectionName}.\nGenerated by Roommatik Analytics.`,
      schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
    },
    item: Object.entries(folders).map(([name, items]) => ({
      name,
      item: items,
    })),
    variable: [
      { key: 'baseUrl', value: extractBaseUrl(requests[0]?.url || ''), type: 'string' },
      { key: 'apiKey', value: requests.find(r => r.headers['X-Api-Key'])?.headers['X-Api-Key'] || '', type: 'string' },
      { key: 'deviceId', value: requests.find(r => r.headers['X-Device-Id'])?.headers['X-Device-Id'] || '', type: 'string' },
    ],
  };

  return JSON.stringify(collection, null, 2);
};

/**
 * Generate a .http file (VS Code REST Client format)
 */
export const toHttpFile = (requests: CapturedRequest[]): string => {
  const baseUrl = extractBaseUrl(requests[0]?.url || '');
  const lines: string[] = [];

  lines.push(`@baseUrl = ${baseUrl}`);
  const apiKey = requests.find(r => r.headers['X-Api-Key'])?.headers['X-Api-Key'];
  if (apiKey) lines.push(`@apiKey = ${apiKey}`);
  const deviceIdHeader = requests.find(r => r.headers['X-Device-Id'])?.headers['X-Device-Id'];
  if (deviceIdHeader) lines.push(`@deviceId = ${deviceIdHeader}`);
  lines.push('');

  for (const req of requests) {
    lines.push(`### ${req.label}`);
    const path = extractPath(req.url);
    lines.push(`${req.method} {{baseUrl}}${path}`);

    Object.entries(req.headers).forEach(([key, value]) => {
      if (key === 'X-Api-Key') {
        lines.push(`${key}: {{apiKey}}`);
      } else if (key === 'X-Device-Id') {
        lines.push(`${key}: {{deviceId}}`);
      } else {
        lines.push(`${key}: ${value}`);
      }
    });

    if (req.body) {
      lines.push('');
      lines.push(JSON.stringify(req.body, null, 2));
    }

    lines.push('');
  }

  return lines.join('\n');
};

/**
 * Trigger browser download of a text file
 */
export const downloadFile = (content: string, filename: string, mimeType: string): void => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// --- Helpers ---

const getFolderName = (label: string): string => {
  if (label.startsWith('Register')) return '1. Registration';
  if (label.startsWith('Send Event')) return '2. Events';
  if (label.startsWith('Send Check-in')) return '3. Check-in';
  if (label.startsWith('Update Journey')) return '4. Journey Updates';
  return '5. Other';
};

const extractBaseUrl = (url: string): string => {
  try {
    const u = new URL(url);
    return `${u.protocol}//${u.host}`;
  } catch {
    return url;
  }
};

const extractPath = (url: string): string => {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
};

const replaceUrlWithVariable = (url: string): string => {
  const path = extractPath(url);
  return `{{baseUrl}}${path}`;
};

const replaceWithVariables = (key: string, value: string): string => {
  if (key === 'X-Api-Key') return '{{apiKey}}';
  if (key === 'X-Device-Id') return '{{deviceId}}';
  return value;
};
